import KeyValueListDisplay from '@/components/KeyValueDisplay';
import { convertISODateToMMDDYYYY } from '@/lib/utils';
import { useDdiReports } from '@/services/dueDiligenceService';
import type { DdiWorkOrderDetailResponse } from '@/types/dueDiligence.types';
import { DDI_RUSH_TYPE_OPTIONS } from '@/utils/constant';

type Props = {
  workOrder: DdiWorkOrderDetailResponse;
};

const formatAddress = (
  line1: string | null,
  line2: string | null,
  city: string | null,
  state: string | null,
  zipCode: string | null
) =>
  [line1, line2, [city, state, zipCode].filter(Boolean).join(', ')].filter(Boolean).join(', ');

const hasNonEmptyAddressPart = (...parts: (string | null)[]) =>
  parts.some((part) => typeof part === 'string' && part.trim().length > 0);

export const SearchDetailsDdiContent = ({ workOrder }: Props) => {
  const { clientDetails, searchDetails, commonDetails } = workOrder;
  const { data: reports } = useDdiReports(clientDetails.typeId, clientDetails.subTypeId);

  const reportNames = searchDetails.reportIds
    .map((id) => reports?.find((r) => r.id === id)?.name)
    .filter((name): name is string => !!name);

  const address = formatAddress(
    searchDetails.addressLine1,
    searchDetails.addressLine2,
    searchDetails.city,
    searchDetails.state,
    searchDetails.zipCode
  );

  const hasAlternateAddress = hasNonEmptyAddressPart(
    searchDetails.alternativeAddressLine1,
    searchDetails.alternativeAddressLine2,
    searchDetails.alternativeAddressCity,
    searchDetails.alternativeAddressState,
    searchDetails.alternativeAddressZipCode
  );

  const alternateAddress = hasAlternateAddress
    ? formatAddress(
        searchDetails.alternativeAddressLine1,
        searchDetails.alternativeAddressLine2,
        searchDetails.alternativeAddressCity,
        searchDetails.alternativeAddressState,
        searchDetails.alternativeAddressZipCode
      )
    : '';

  const searchDetailsFields = [
    ...(searchDetails.personForInquiry
      ? [{ label: 'Person for Inquiry', value: searchDetails.personForInquiry }]
      : []),
    ...(searchDetails.entityForInquiry
      ? [{ label: 'Entity for Inquiry', value: searchDetails.entityForInquiry }]
      : []),
    ...(searchDetails.vin
      ? [{ label: 'Vehicle Identification Number (VIN)', value: searchDetails.vin }]
      : []),
    ...(searchDetails.addressType ? [{ label: 'Address Type', value: searchDetails.addressType }] : []),
    { label: 'Address', value: address || '-' },
    ...(hasAlternateAddress ? [{ label: 'Alternate Address', value: alternateAddress }] : []),
    { label: 'Due Date', value: convertISODateToMMDDYYYY(commonDetails.dueDate) },
    { label: 'Report', value: reportNames.length > 0 ? reportNames.join(', ') : '-' },
    {
      label: 'Rush',
      value: commonDetails.isRush
        ? (DDI_RUSH_TYPE_OPTIONS.find((option) => option.value === commonDetails.rushType)?.label ?? 'Rush')
        : 'No',
    },
    { label: 'Notes', value: searchDetails.notes ?? '-' },
    { label: 'Assigned User', value: searchDetails.assignedUser?.name ?? '-' },
    { label: 'Created On', value: convertISODateToMMDDYYYY(commonDetails.createdAt) },
    { label: 'Created By', value: commonDetails.createdBy.name },
    ...(commonDetails.updatedAt
      ? [{ label: 'Modified On', value: convertISODateToMMDDYYYY(commonDetails.updatedAt) }]
      : []),
    ...(commonDetails.modifiedBy ? [{ label: 'Modified By', value: commonDetails.modifiedBy.name }] : []),
  ];

  return (
    <div className="flex flex-col gap-2 w-full px-6 py-6 text-sm">
      <KeyValueListDisplay data={searchDetailsFields} />
    </div>
  );
};

export default SearchDetailsDdiContent;
